import { RESPONDER_PHASES } from './shared-responder-v2-core-session.consts';
import { selectIsStepAnswered } from './shared-responder-v2-core-session.selectors';
import type {
  ResponderSessionState,
  ResponderStep,
} from './shared-responder-v2-core-session.types';

export interface FinalSendBlock {
  readonly firstOwedIndex: number;
}

/**
 * Required questions still owed, in step order. Answered follows `selectIsStepAnswered`: a
 * `Closed` thread counts as done regardless of `answer`, because a deliberate skip
 * (`resolveForwardIntent`'s non-required incomplete-draft branch) can close a thread without
 * ever writing one, while a `Failed` thread does not since the backend never accepted its answer.
 * A question a branch rule routed around is never owed. Returns the same step references as
 * `state.steps`, since the progress list renders `step.label.text`.
 */
export function selectIncompleteRequiredSteps(
  state: ResponderSessionState
): readonly ResponderStep[] {
  return state.steps.filter(
    (step) =>
      step.isRequired &&
      !state.skippedQuestionIds.includes(step.questionId) &&
      !selectIsStepAnswered(state, step.questionId)
  );
}

export function canFinish(state: ResponderSessionState): boolean {
  return selectIncompleteRequiredSteps(state).length === 0;
}

/**
 * Step index to send the responder back to for an owed required question, or `null` if none
 * are owed. Treats the current index as not-owed, since bouncing to the on-screen question
 * would be a no-op dead button.
 */
export function selectFirstIncompleteRequiredIndex(
  state: ResponderSessionState,
  questionIds?: readonly string[]
): number | null {
  const owed =
    questionIds === undefined
      ? selectIncompleteRequiredSteps(state).map((step) => step.questionId)
      : questionIds;

  for (const questionId of owed) {
    const index = state.steps.findIndex(
      (step) => step.questionId === questionId
    );

    if (index !== -1 && index !== state.currentStepIndex) {
      return index;
    }
  }

  return null;
}

export function selectFinalSendBlock(
  state: ResponderSessionState
): FinalSendBlock | null {
  if (
    state.phase !== RESPONDER_PHASES.Question ||
    state.editing.mode === 'editing' ||
    state.currentStepIndex !== state.steps.length - 1
  ) {
    return null;
  }

  const firstOwedIndex = selectFirstIncompleteRequiredIndex(state);

  return firstOwedIndex === null ? null : { firstOwedIndex };
}
